import { useMediaQuery, useTheme, Box } from "@mui/material";
import { TaskAltOutlined } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";

const UseCaseCard = ({ img, title, useCaseData }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down(480));

  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const slideInStyle = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(30px)",
    transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const staggeredSlideIn = (delay) => ({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(30px)",
    transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
  });

  return (
    <Box
      ref={cardRef}
      fullWidth
      sx={{
        width: "100%",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: "1rem", ...slideInStyle }}>
        <h1
          style={{
            fontSize: isSmall ? "1rem" : "2rem",
            fontWeight: "bold",
            margin: "0",
            color: "#333",
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: isSmall ? "0.75rem" : "1.2rem",
            color: "#666",
            lineHeight: 1.6,
            maxWidth: "1000px",
            margin: "0",
          }}
        >
          {useCaseData.longDescription}
        </p>
      </div>

      {/* Main Content Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isSmall ? "1fr" : "1fr 1fr",
          gap: isSmall ? "1rem" : "2rem",
          marginBottom: "1rem",
        }}
      >
        {/* Inclus Section */}
        <div style={staggeredSlideIn(200)}>
          <h3
            style={{
              fontSize: isSmall ? "1rem" : "2rem",
              fontWeight: "bold",
              margin: "0",
              color: "#333",
            }}
          >
            Inclus
          </h3>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {useCaseData.inclus?.map((item, index) => (
              <li
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#666",
                  gap: "0.5rem",
                }}
              >
                <TaskAltOutlined sx={{ color: "#d12121" }} />
                <p
                  style={{
                    fontSize: isSmall ? "0.75rem" : "1.2rem",
                    color: "#666",
                    lineHeight: 1.6,
                    maxWidth: "1000px",
                    margin: "0",
                  }}
                >
                  {item}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Tarif Section */}
        <div style={staggeredSlideIn(400)}>
          <h3
            style={{
              fontSize: isSmall ? "1rem" : "2rem",
              fontWeight: "bold",
              margin: "0",
              color: "#333",
            }}
          >
            Tarif
          </h3>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {useCaseData.tarifs?.map((tarif, index) => (
              <li
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "#666",
                  gap: "0.5rem",
                }}
              >
                <TaskAltOutlined sx={{ color: "#d12121" }} />
                <p
                  style={{
                    fontSize: isSmall ? "0.75rem" : "1.2rem",
                    color: "#666",
                    lineHeight: 1.6,
                    maxWidth: "1000px",
                    margin: "0",
                  }}
                >
                  {tarif}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Services Section */}
        <div style={staggeredSlideIn(600)}>
          {useCaseData.services && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              {useCaseData.services.map((service, index) => (
                <div
                  key={index}
                  style={{
                    textAlign: "center",
                    padding: "1rem 0.5rem",
                    background: "#E8E6E350",
                    borderRadius: "8px",
                    // Conditional styling for full width
                    ...(service.name === "Clip vidéo, shooting photo" && {
                      gridColumn: "1 / -1",
                      width: "100%",
                    }),
                  }}
                >
                  <div
                    style={{
                      fontSize: 30,
                      backgroundColor: "#d12121",
                      color: "#fff",
                      width: 60,
                      height: 60,
                      margin: "0 auto",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 8px rgba(200, 70, 83, 0.3)",
                    }}
                  >
                    <img
                      src={service.icon}
                      alt={service.name}
                      style={{ width: 30, height: 30 }}
                    />
                  </div>
                  <h4
                    style={{
                      fontSize: isSmall ? "0.75rem" : "0.9rem",
                      fontWeight: "bold",
                      margin: 0,
                      color: "#333",
                      marginTop: "1rem",
                    }}
                  >
                    {service.name}
                  </h4>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image Section */}
        <div
          style={{
            ...staggeredSlideIn(600),
            order: isExtraSmall ? -1 : 0,
          }}
        >
          <img
            src={img}
            alt={title}
            style={{
              width: "100%",
              height: isSmall ? "200px" : "300px",
              objectFit: "cover",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>
    </Box>
  );
};

export default UseCaseCard;
